import { useMutation, useQueryClient } from "@tanstack/react-query"
import { FileImage, FileText, FileX2, Loader2 } from "lucide-react"
import { useState } from "react"

import {
  Button,
  cn,
  Skeleton,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  toast,
} from "@atlant/ui"

import { graphql } from "#gql"
import { queryKeys } from "#lib/query-keys.ts"
import { useLoggedInUserInfo } from "#store/user-info.ts"
import { getFileUrl } from "#utils/file-url.ts"
import { client } from "#utils/graphql-client.ts"

import { headClass, SortableHead, type SortDir } from "../../components/sortable-head.tsx"
import { StatusBadge, type InvoiceStatus } from "../../components/status-badge.tsx"
import type { InvoiceUploadRow } from "../upload-queue.tsx"

const InvoiceUploadApproveDocument = graphql(/* GraphQL */ `
  mutation InvoiceUploadApprove($input: MutationInvoiceUploadApproveInput!) {
    invoiceUploadApprove(input: $input) {
      id
      status
      approvedAt
    }
  }
`)

const COLUMN_COUNT = 9

const statusBadge = {
  CLASSIFYING: "classifying",
  AWAITING_REVIEW: "awaitingReview",
  APPROVED: "approved",
} as const satisfies Record<InvoiceUploadRow["status"], InvoiceStatus>

// "124 KB", "1.2 MB"
const formatFileSize = (bytes: number) =>
  bytes < 1024 * 1024
    ? `${Math.max(1, Math.round(bytes / 1024))} KB`
    : `${(bytes / (1024 * 1024)).toFixed(1)} MB`

// The rows scroll under the header, so it is opaque: the muted/50 tint baked onto the
// card. Borders sit on the cells, as a collapsed table's borders do not stick
const stickyHead =
  "sticky top-0 z-10 border-b bg-[color-mix(in_oklab,var(--muted)_50%,var(--card))]"

type SortKey = "fileName" | "vendor" | "serviceMonth" | "createdDate"

const compare = (a: InvoiceUploadRow, b: InvoiceUploadRow, key: SortKey) =>
  key === "createdDate"
    ? Number(a.createdDate.milliseconds) - Number(b.createdDate.milliseconds)
    : // serviceMonth is YYYY-MM, which sorts as text
      (a[key] ?? "").localeCompare(b[key] ?? "")

interface UploadQueueTableProps {
  rows: InvoiceUploadRow[]
  isLoading: boolean
  emptyText: string
}

export const UploadQueueTable = ({
  rows,
  isLoading,
  emptyText,
}: UploadQueueTableProps) => {
  const { me } = useLoggedInUserInfo()
  const [sort, setSort] = useState<{ key: SortKey; dir: SortDir }>({
    key: "createdDate",
    dir: "desc",
  })

  const sorted = rows.toSorted((a, b) => {
    const result = compare(a, b, sort.key)
    return (sort.dir === "asc" ? result : -result) || a.id.localeCompare(b.id)
  })

  const onSort = (key: SortKey) =>
    setSort((prev) =>
      prev.key === key
        ? { key, dir: prev.dir === "asc" ? "desc" : "asc" }
        : { key, dir: "asc" },
    )

  const sortable = (key: SortKey, label: string, className?: string) => (
    <SortableHead
      label={label}
      active={sort.key === key}
      dir={sort.dir}
      onSort={() => onSort(key)}
      className={cn(stickyHead, className)}
    />
  )

  const head = cn(headClass, stickyHead)

  return (
    // The table scrolls on its own, under a sticky header; separate borders so they scroll too
    <div className="min-h-0 overflow-auto rounded-lg border">
      <table className="w-full border-separate border-spacing-0 text-sm">
        <TableHeader>
          <TableRow className="hover:bg-transparent">
            {sortable("fileName", "File name", "w-full")}
            {/* Guessed by AI classification */}
            <TableHead className={head}>Late</TableHead>
            {sortable("vendor", "Vendor (guessed)")}
            <TableHead className={head}>Confidence</TableHead>
            {sortable("serviceMonth", "Service month")}
            <TableHead className={head}>Status</TableHead>
            {sortable("createdDate", "Imported at")}
            <TableHead className={cn(head, "text-right")}>Size</TableHead>
            <TableHead className={cn(head, "text-right")}>Action</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody className="[&_td]:border-b [&_tr:last-child>td]:border-b-0">
          {isLoading &&
            Array.from({ length: 6 }, (_, i) => (
              <TableRow key={i}>
                <TableCell colSpan={COLUMN_COUNT}>
                  <Skeleton className="h-5 w-full" />
                </TableCell>
              </TableRow>
            ))}

          {!isLoading && sorted.length === 0 && (
            <TableRow>
              <TableCell
                colSpan={COLUMN_COUNT}
                className="text-muted-foreground py-8 text-center"
              >
                {emptyText}
              </TableCell>
            </TableRow>
          )}

          {!isLoading &&
            sorted.map((upload) => {
              const Icon = upload.mimeType === "application/pdf" ? FileText : FileImage
              // A missing guess says why, when classification gave up on the file
              const empty = <span title={upload.classificationNote ?? undefined}>—</span>
              return (
                <TableRow
                  key={upload.id}
                  // Late rows are tinted, as in the design
                  className={cn(upload.late && "bg-primary/7 hover:bg-primary/10")}
                >
                  <TableCell className="max-w-64">
                    <a
                      href={getFileUrl(
                        me.organization.id,
                        "invoice",
                        upload.blobName,
                        "private",
                      )}
                      target="_blank"
                      rel="noreferrer"
                      title={upload.fileName}
                      // Default cursor, as on the app's other clickable rows and cards
                      className="flex cursor-default items-center gap-2 hover:underline"
                    >
                      <Icon className="text-primary size-4 shrink-0" />
                      <span className="truncate">{upload.fileName}</span>
                    </a>
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {/* Blank when on time; a dash when there is no month to tell */}
                    {upload.late ? (
                      <span title="Late invoice" className="flex">
                        <FileX2
                          aria-label="Late invoice"
                          className="size-4 text-fuchsia-600 dark:text-fuchsia-300"
                        />
                      </span>
                    ) : (
                      upload.late === null && empty
                    )}
                  </TableCell>
                  <TableCell className="max-w-40">
                    {upload.vendor ? (
                      <div className="truncate" title={upload.vendor}>
                        {upload.vendor}
                      </div>
                    ) : (
                      <span className="text-muted-foreground">{empty}</span>
                    )}
                    {/* Attached to its PUR's invoice rather than posted as one */}
                    {upload.documentKind === "SUPPORTING" && (
                      <div className="text-muted-foreground text-xs">
                        Supporting document
                      </div>
                    )}
                    {/* Why there is nothing to approve */}
                    {upload.postError && (
                      <div
                        className="text-muted-foreground truncate text-xs"
                        title={upload.postError}
                      >
                        {upload.postError}
                      </div>
                    )}
                  </TableCell>
                  {/* Left empty until its display is designed */}
                  <TableCell />
                  <TableCell className="whitespace-nowrap">
                    {upload.serviceMonthDisplay ?? (
                      <span className="text-muted-foreground">{empty}</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <StatusBadge status={statusBadge[upload.status]} />
                  </TableCell>
                  <TableCell className="whitespace-nowrap">
                    <div>{upload.createdDate.displaydate}</div>
                    <div className="text-muted-foreground text-xs">
                      {upload.createdDate.displaytime}
                    </div>
                  </TableCell>
                  <TableCell className="text-right whitespace-nowrap tabular-nums">
                    {formatFileSize(upload.size)}
                  </TableCell>
                  <TableCell className="text-right">
                    <ApproveButton upload={upload} />
                  </TableCell>
                </TableRow>
              )
            })}
        </TableBody>
      </table>
    </div>
  )
}

/** Classification already posted it; Approve only signs off, so it needs an invoice. */
const ApproveButton = ({ upload }: { upload: InvoiceUploadRow }) => {
  const { me } = useLoggedInUserInfo()
  const organizationId = me.organization.id
  const queryClient = useQueryClient()

  const { mutate, isPending } = useMutation({
    mutationFn: () =>
      client.request(InvoiceUploadApproveDocument, {
        input: { organizationId, id: upload.id },
      }),
    // Stay pending until the refetched row shows the new status
    onSuccess: () => {
      toast.success(`Approved ${upload.fileName}`)
      return queryClient.invalidateQueries({
        queryKey: queryKeys.invoices.uploads(organizationId),
      })
    },
  })

  return (
    <Button
      variant="outline"
      size="sm"
      className="text-primary hover:text-primary disabled:text-muted-foreground font-semibold"
      disabled={upload.status !== "AWAITING_REVIEW" || !upload.invoiceId || isPending}
      onClick={() => mutate()}
    >
      {isPending && <Loader2 className="animate-spin" />}
      Approve
    </Button>
  )
}
